import { Auth } from "utils/cognito-auth";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  ClipConfiguration,
  PaginatedClipsResponse,
  QuoteSummary,
} from "pages/geoscape-data/clip/clip-ui/models";
import { ClipProductResponse } from "pages/geoscape-data/clip/clip-ui/catalogue/catalogue";
import { TileJSON } from "pages/geoscape-data/explorer/layers/models";
import {
  Address,
  CRS,
  Suggest,
} from "pages/geoscape-demos/demos/predictive-address/models";
import {
  Locality,
  SuggestLocality,
} from "pages/geoscape-demos/demos/predictive-suburb/models";
import { makeApiRequestPostToUrl, makeApiRequestToUrl } from "utils/api-client";
import {
  ApiKey,
  ApiProduct,
  CreditsInfo,
  CustomDatasetDefinitions,
  CustomDatasetFilterType,
  CustomDatasetList,
  DatasetDefinitions,
  DatasetFiltersType,
  DatasetList,
  DemoConfiguration,
  DeploymentInfo,
  MemberAccountDetails,
  OrgMembership,
  Organisation,
  PaymentMethod,
  PlanInfo,
  PlanPermissions,
  Subscription,
  UserLicences,
  UserMembership,
  UserStatus,
} from "./models";

interface ReactQueryQueryError {
  message: string;
  stack: string;
}

async function fetchOrganisation(): Promise<Organisation> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/organisation",
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch organisation: ${err}`);
  }
}

async function fetchPaymentMethod(): Promise<PaymentMethod | null> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/paymentMethod",
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    const paymentMethods: PaymentMethod[] = responseData.filter(
      (paymentMethod: PaymentMethod) => paymentMethod.active,
    );
    if (paymentMethods.length !== 0) {
      return paymentMethods[0];
    } else {
      return null;
    }
  } catch (err) {
    throw new Error(`Unable to fetch payment method: ${err}`);
  }
}

async function fetchCustomDatasets(
  queryFields: CustomDatasetFilterType,
  limit: number = 100,
  offset: number = 0,
): Promise<CustomDatasetList> {
  try {
    const queryFields_update: any = { ...queryFields };

    queryFields_update.release = queryFields_update.latestRelease
      ? "latest"
      : queryFields_update.release;
    delete queryFields_update.latestRelease;

    queryFields_update.product = queryFields_update.name
      ? queryFields_update.name.map((name: string) => name.toUpperCase())
      : "";
    delete queryFields_update.name;

    let searchParams = new URLSearchParams();
    searchParams.set("limit", "100");
    Object.entries(queryFields_update).forEach(([key, value]: any) =>
      value.toString().length ? searchParams.set(key, value) : "",
    );

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/custom?${searchParams.toString()}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all custom datasets: ${err}`);
  }
}

async function fetchDatasets(
  queryFields: DatasetFiltersType,
  limit: number = 100,
  offset: number = 0,
): Promise<DatasetList> {
  try {
    const queryFields_update: any = { ...queryFields };

    queryFields_update.release = queryFields_update.latestRelease
      ? "latest"
      : queryFields_update.release;
    delete queryFields_update.latestRelease;

    let searchParams = new URLSearchParams();
    searchParams.set("limit", `${limit}`);
    searchParams.set("offset", `${offset}`);
    Object.entries(queryFields_update).forEach(([key, value]: any) =>
      value.toString().length ? searchParams.set(key, value) : "",
    );

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets?${searchParams.toString()}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all datasets: ${err}`);
  }
}

async function fetchDatasetsLink(datasetId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/${datasetId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData.datasets[0].downloadLink;
  } catch (err) {
    throw new Error(`Unable to download dataset '${datasetId}' : ${err}`);
  }
}

async function fetchCustomDatasetsLink(datasetId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/custom/${datasetId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData.datasets[0].downloadLink;
  } catch (err) {
    throw new Error(
      `Unable to download custom dataset '${datasetId}' : ${err}`,
    );
  }
}

async function fetchSubscription(): Promise<Subscription> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/subscriptions",
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch subscription: ${err}`);
  }
}

async function fetchPlans(): Promise<PlanInfo> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/plans",
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch plans: ${err}`);
  }
}

async function fetchCredits(): Promise<CreditsInfo | null> {
  try {
    let response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/credits",
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch credits: ${err}`);
  }
}

async function fetchPermissions(
  permissionSets?: string[],
): Promise<PlanPermissions> {
  try {
    const query = permissionSets?.length
      ? `?permissionSets=${encodeURIComponent(permissionSets.join(","))}`
      : "";

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/permissions${query}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch permissions: ${err}`);
  }
}

async function fetchUserMemberships(
  email: boolean = false,
): Promise<UserMembership[]> {
  try {
    const queryParam = email ? `?byEmail=true` : "";

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/user/membership${queryParam}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch user membership: ${err}`);
  }
}

async function fetchLatestVersion() {
  try {
    const response = await fetch("/version.json", {
      cache: "no-store",
    });
    const data = await response.json();
    const latestVersion = data.version;
    return latestVersion;
  } catch (error) {
    console.error("Error checking for updates:", error);
  }
}

async function fetchUserInvitations(): Promise<UserMembership[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/v2/user/membership?byEmail=true",
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch user invitations: ${err}`);
  }
}

async function fetchUsage(
  startTime: number,
  endTime: number,
  overrideIntervalTime?: string,
) {
  try {
    dayjs.extend(utc);
    dayjs.extend(timezone);

    // Get the timezone string from the user's device
    const userTimezone = dayjs.tz.guess();

    // Construct the query parameters
    let queryParams = `offset=${userTimezone}&startTime=${startTime}&endTime=${endTime}`;

    if (overrideIntervalTime !== undefined) {
      queryParams += `&overrideIntervalTime=${overrideIntervalTime}`;
    }

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/usage?${queryParams}`,
      "GET",
    );

    const responseData = await response.json(); // add type: Usage

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch usage data: ${err}`);
  }
}

async function fetchApiKeys(): Promise<ApiKey[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/apiKeys`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch API keys: ${err}`);
  }
}

async function fetchDefinitions(): Promise<DatasetDefinitions> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/definitions`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch dataset definitions: ${err}`);
  }
}

async function fetchCustomDefinitions(): Promise<CustomDatasetDefinitions> {
  let products;

  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/custom/definitions`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch custom dataset definitions: ${err}`);
  }
}

async function fetchApiProducts(): Promise<ApiProduct[]> {
  let apiProducts;

  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/products`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch API products data: ${err}`);
  }
}

async function fetchOrganisationMemberships(
  organisationId: string,
): Promise<UserMembership[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/membership?organisationId=${organisationId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch organisation invitations for organisation '${organisationId}' : ${err}`,
    );
  }
}

async function fetchMemberUserDetails(
  accountId: string,
): Promise<MemberAccountDetails> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/member/${accountId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch user details for account '${accountId}' : ${err}`,
    );
  }
}
async function fetchUserOrgMembership(
  accountId: string,
): Promise<OrgMembership> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/v2/organisation/membership/${accountId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch user details as admin for account '${accountId}' : ${err}`,
    );
  }
}

async function checkDatasetsAccessibility(): Promise<boolean> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/datasets/beta",
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      return false;
    }

    return responseData;
  } catch (err) {
    return false;
  }
}

async function fetchCustomDatasetsDefinitions(
  developerId: string,
): Promise<CustomDatasetDefinitions> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/custom/definitions?developerId=${developerId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch custom dataset definitions for user '${developerId}' : ${err}`,
    );
  }
}

async function fetchUserLicences(): Promise<UserLicences> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/licences`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch user licences: ${err}`);
  }
}

async function fetchIntercomIdentityHash(): Promise<string> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/intercom/user_hash`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch intercom user hash: ${err}`);
  }
}

async function fetchAuthJWT() {
  return (await Auth.currentSession()).getIdToken().getJwtToken();
}

async function fetchMapsIndex(authorisation: string): Promise<TileJSON[]> {
  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_API_BASE_URL
      }/v1/explorer/index.json?key=${authorisation}`,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch maps index: ${err}`);
  }
}

async function fetchMapsLayerJson(
  authorisation: string,
  layer: string,
): Promise<TileJSON> {
  try {
    const response = await fetch(
      `${
        import.meta.env.VITE_API_BASE_URL
      }/v1/explorer/geoscape_v1/${layer}.json?key=${authorisation}`,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch maps ${layer} layer json: ${err}`);
  }
}

async function fetchBatches(sourceOrgId?: string): Promise<any> {
  try {
    const queryParams = new URLSearchParams();
    if (sourceOrgId) {
      queryParams.append("sourceOrgId", sourceOrgId);
    }

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/config?${queryParams.toString()}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all batch jobs: ${err}`);
  }
}

// Fetch a single batch config
async function fetchBatchConfig(batchId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/config/${batchId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch batch job '${batchId}' : ${err}`);
  }
}

async function fetchBatchesDefinitions(): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/definitions`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch batch definitions: ${err}`);
  }
}

async function fetchBatchesAdditionalProperties(): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/additionalProperties`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch batch additional properties: ${err}`);
  }
}

async function fetchAddressById(
  addressId: string,
  crs?: CRS,
): Promise<Address> {
  try {
    const headers = new Headers();
    if (crs) headers.append("Accept-Crs", crs);

    const response = await fetch(
      `https://api.psma.com.au/v1/predictive-demo/address/${addressId}`,
      {
        headers: headers,
      },
    );
    const basictransaction = response.headers.get("x-attr-basictransaction");
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return { ...responseData, basictransaction: Number(basictransaction) };
  } catch (err) {
    throw new Error(`Unable to fetch address by id '${addressId}' : ${err}`);
  }
}

async function fetchPredictive(
  query: string,
  dataset: string,
  excludeAliases: boolean,
  addressType: string,
  stateTerritory: string,
  maxNumberOfResults: number,
  excludePrincipalWithSec: boolean,
): Promise<Suggest> {
  try {
    const response = await fetch(
      `https://api.psma.com.au/v1/predictive-demo/address?` +
        new URLSearchParams({
          query: query,
          dataset: dataset,
          excludeAliases: excludeAliases.toString(),
          addressType: addressType,
          stateTerritory: stateTerritory,
          maxNumberOfResults: maxNumberOfResults.toString(),
          excludePrincipalWithSec: excludePrincipalWithSec.toString(),
        }).toString(),
    );
    const responseData = await response.json();
    const basictransaction = response.headers.get("x-attr-basictransaction");

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return { ...responseData, basictransaction: Number(basictransaction) };
  } catch (err) {
    throw new Error(`Unable to fetch predictive addresses '${query}' : ${err}`);
  }
}

async function fetchPredictiveLocality(
  query: string,
  postcode: string,
  stateTerritory: string,
  maxNumberOfResults: number,
): Promise<SuggestLocality> {
  try {
    const response = await fetch(
      `https://api.psma.com.au/v1/predictive-demo/locality?` +
        new URLSearchParams({
          query: query,
          postcode: postcode,
          stateTerritory: stateTerritory,
          maxNumberOfResults: maxNumberOfResults.toString(),
        }).toString(),
    );

    const responseData = await response.json();
    const basictransaction = response.headers.get("x-attr-basictransaction");

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return { ...responseData, basictransaction: Number(basictransaction) };
  } catch (err) {
    throw new Error(
      `Unable to fetch predictive localities '${query}' : ${err}`,
    );
  }
}

async function fetchAdminBounds(
  identifier: string,
  layers: string,
  excludeGeometry: boolean,
  crs?: CRS,
): Promise<Locality> {
  try {
    const headers = new Headers();
    if (crs) headers.append("Accept-Crs", crs);

    const response = await fetch(
      `https://api.psma.com.au/v1/admin-bounds-demo/findByIdentifier?` +
        new URLSearchParams({
          identifier: identifier,
          layers: layers,
          excludeGeometry: excludeGeometry.toString(),
        }).toString(),
      {
        headers: headers,
      },
    );

    const responseData = await response.json();
    const basictransaction = response.headers.get("x-attr-basictransaction");

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return { ...responseData, basictransaction: Number(basictransaction) };
  } catch (err) {
    throw new Error(
      `Unable to fetch admin bounds for  identifier '${identifier}' : ${err}`,
    );
  }
}

async function fetchClips(): Promise<ClipConfiguration[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/config`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all clips: ${err}`);
  }
}

async function fetchClipsPaginated(
  pageParam?: number,
  limit?: number,
  includeCredits?: boolean,
): Promise<PaginatedClipsResponse> {
  try {
    const params = new URLSearchParams();
    if (pageParam) {
      params.set("last_key", pageParam.toString());
    }
    if (limit) {
      params.set("limit", limit.toString());
    }
    if (includeCredits) {
      params.set("include_credits", "true");
    }
    const queryString = params.toString();

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/config/paginated${queryString ? `?${queryString}` : ""}`,
      "GET",
    );
    const responseData = await response.json();
    if (response.status !== 200) {
      throw new Error(
        responseData.messages?.join(". ") || "Failed to fetch clips",
      );
    }
    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch paginated clips: ${err}`);
  }
}

export async function fetchAndParse(url: string): Promise<GeoJSON.FeatureCollection> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`S3 responded ${response.status}`);
  return (await response.json()) as GeoJSON.FeatureCollection;
}

// Clips saved before S3 storage carry their boundary inline.
async function fetchClipGeometry(
  clip: ClipConfiguration,
): Promise<GeoJSON.FeatureCollection | null> {
  if (clip.geometry) return clip.geometry;
  if (clip.geometryUrl) return fetchAndParse(clip.geometryUrl);
  const detailedClip = await fetchClipConfig(clip.id);
  if (detailedClip.geometry) return detailedClip.geometry;
  if (!detailedClip.geometryUrl) return null;
  return fetchAndParse(detailedClip.geometryUrl);
}

// Fetch a single clip config
async function fetchClipConfig(clipId: string): Promise<ClipConfiguration> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/config/${clipId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch clip config '${clipId}' : ${err}`);
  }
}

const fetchBatchSample = async (
  batchId: string,
  body: any,
  jobType: string,
) => {
  try {
    const response = await makeApiRequestPostToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/batches/sample?jobType=${jobType}`,
      "POST",
      body,
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch sample results for batch '${batchId}' : ${err}`,
    );
  }
};
// Fetch a single clip summary
async function fetchClipSummary(clipId: string): Promise<QuoteSummary> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/summary/${clipId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch clip summary for '${clipId}' : ${err}`);
  }
}

// Fetch the Clip product catalogue (categories → products → layers), enriched
// with clip pricing and product relationships. Served by GET /clip/products.
async function fetchClipProducts(): Promise<ClipProductResponse[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/clip/products`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch clip products : ${err}`);
  }
}

async function fetchLandParcelsProperties(addressId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/landParcels/properties/findByIdentifier?addressId=${addressId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch land parcels properties for address id ${addressId}: ${err}`,
    );
  }
}

async function fetchLandParcelsCadastresId(
  cadastrePid: string,
): Promise<GeoJSON.FeatureCollection> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/landParcels/cadastres/${cadastrePid}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch land parcels cadastres for cadastre pid ${cadastrePid}: ${err}`,
    );
  }
}

async function fetchLandParcelsCadastres(addressId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/landParcels/cadastres/findByIdentifier?addressId=${addressId}&additionalProperties=planningBundle`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch land parcels cadastres for address id ${addressId}: ${err}`,
    );
  }
}

async function fetchBuildingsByAddressId(addressId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/buildings?addressId=${addressId}&include=all`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch buildings for address id ${addressId}: ${err}`,
    );
  }
}

async function fetchBuildingsByIdentifier(
  idKey: string,
  idValue: string,
  additionalProperties: string[],
): Promise<GeoJSON.FeatureCollection> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/buildings/findByIdentifier?${idKey}=${idValue}&additionalProperties=${additionalProperties.join(
        ",",
      )}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch buildings for ${idKey} ${idValue}: ${err}`,
    );
  }
}

async function fetchUserStatus(): Promise<UserStatus> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/user/status",
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch user status. ${err}`);
  }
}

async function fetchDemos(): Promise<DemoConfiguration[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/demos",
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch demos. ${err}`);
  }
}

export type { ReactQueryQueryError };

// CMS / Blog Articles
import type {
  StrapiArticlesResponse,
  StrapiSingleArticleResponse,
} from "components/blog/types";

async function fetchArticles(
  page: number = 1,
  pageSize: number = 25,
): Promise<StrapiArticlesResponse> {
  const cmsUrl = import.meta.env.VITE_CMS_URL;

  try {
    const response = await fetch(
      `${cmsUrl}/api/articles?pagination[page]=${page}&pagination[pageSize]=${pageSize}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return await response.json();
  } catch (err) {
    throw new Error(`Unable to fetch articles: ${err}`);
  }
}

async function fetchArticle(
  identifier: string,
): Promise<StrapiSingleArticleResponse> {
  const cmsUrl = import.meta.env.VITE_CMS_URL;

  try {
    // Use Strapi's $or filter to match either slug or documentId
    const response = await fetch(
      `${cmsUrl}/api/articles?filters[$or][0][slug][$eq]=${identifier}&filters[$or][1][documentId][$eq]=${identifier}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    return await response.json();
  } catch (err) {
    throw new Error(`Unable to fetch article: ${err}`);
  }
}

async function fetchDeploymentStatus(
  projectName: string,
): Promise<DeploymentInfo> {
  try {
    // In preprod, /ops/* routes through CloudFront to DevOps API
    // Locally, fetch from preprod hub directly
    const baseUrl =
      import.meta.env.VITE_ENVIRONMENT === "preprod"
        ? ""
        : // This case is if you are doing local dev on the widget and want to hit preprod
          // from your local
          "https://preprod-hub.geoscape.com.au";

    const response = await fetch(
      `${baseUrl}/ops/v1/deployments/${projectName}`,
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  } catch (err) {
    throw new Error(
      `Unable to fetch deployment status for '${projectName}': ${err}`,
    );
  }
}

export {
  checkDatasetsAccessibility,
  fetchAddressById,
  fetchAdminBounds,
  fetchApiKeys,
  fetchApiProducts,
  fetchArticle,
  fetchArticles,
  fetchAuthJWT,
  fetchBatchConfig,
  fetchBatchSample,
  fetchBatches,
  fetchBatchesAdditionalProperties,
  fetchBatchesDefinitions,
  fetchBuildingsByAddressId,
  fetchBuildingsByIdentifier,
  fetchClipConfig,
  fetchClipGeometry,
  fetchClipProducts,
  fetchClipSummary,
  fetchClips,
  fetchClipsPaginated,
  fetchCredits,
  fetchCustomDatasets,
  fetchCustomDatasetsDefinitions,
  fetchCustomDatasetsLink,
  fetchCustomDefinitions,
  fetchDatasets,
  fetchDatasetsLink,
  fetchDefinitions,
  fetchDemos,
  fetchDeploymentStatus,
  fetchIntercomIdentityHash,
  fetchLandParcelsCadastres,
  fetchLandParcelsCadastresId,
  fetchLandParcelsProperties,
  fetchLatestVersion,
  fetchMapsIndex,
  fetchMapsLayerJson,
  fetchMemberUserDetails,
  fetchOrganisation,
  fetchOrganisationMemberships,
  fetchPaymentMethod,
  fetchPermissions,
  fetchPlans,
  fetchPredictive,
  fetchPredictiveLocality,
  fetchSubscription,
  fetchUsage,
  fetchUserInvitations,
  fetchUserLicences,
  fetchUserMemberships,
  fetchUserOrgMembership,
  fetchUserStatus,
};
